import { apiClient, handleApiRequest } from './client'
import type {
  ChatOverviewResponse,
  ChatUnreadCountResponse,
  ChatParticipantResponse,
  ChatMessageResponse,
  ChatMessagePage,
  ScopeFilter,
  ScopeCursor,
} from '@/types/teacher-chat'

export const teacherChatApi = {
  getChats: () =>
    handleApiRequest<ChatOverviewResponse>(
      apiClient.get('/api/v1/teacher-chats')
    ),

  getUnreadCount: () =>
    handleApiRequest<ChatUnreadCountResponse>(
      apiClient.get('/api/v1/teacher-chats/unread-count')
    ),

  getParticipants: (requestId: string) =>
    handleApiRequest<ChatParticipantResponse[]>(
      apiClient.get(`/api/v1/teacher-chats/${requestId}/participants`)
    ),

  addParticipant: (requestId: string, teacherId: string) =>
    handleApiRequest<ChatParticipantResponse>(
      apiClient.post(`/api/v1/teacher-chats/${requestId}/participants`, { teacher_id: teacherId })
    ),

  removeParticipant: (requestId: string, teacherId: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.delete(`/api/v1/teacher-chats/${requestId}/participants/${teacherId}`)
    ),

  getMessages: (
    requestId: string,
    limit = 50,
    cursor?: ScopeCursor,
    scopeFilter?: ScopeFilter
  ) => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (cursor) {
      params.append('before', cursor.before)
      params.append('before_id', cursor.before_id)
    }
    if (scopeFilter?.scope_filter_active) {
      params.append('scope_filter_active', 'true')
    }
    if (scopeFilter?.scope_filter_lesson_ids?.length) {
      scopeFilter.scope_filter_lesson_ids.forEach(id =>
        params.append('scope_filter_lesson_ids', id)
      )
    }
    if (scopeFilter?.teacher_filter_id) {
      params.append('teacher_filter_id', scopeFilter.teacher_filter_id)
    }
    return handleApiRequest<ChatMessagePage>(
      apiClient.get(`/api/v1/teacher-chats/${requestId}/messages?${params}`)
    )
  },

  sendMessage: (requestId: string, body: string, scopeLessonIds?: string[]) =>
    handleApiRequest<ChatMessageResponse>(
      apiClient.post(`/api/v1/teacher-chats/${requestId}/messages`, {
        body,
        scope_lesson_ids: scopeLessonIds?.length ? scopeLessonIds : undefined,
      })
    ),

  deleteMessage: (requestId: string, messageId: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.delete(`/api/v1/teacher-chats/${requestId}/messages/${messageId}`)
    ),

  markAsRead: (requestId: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.post(`/api/v1/teacher-chats/${requestId}/mark-read`)
    ),

  archiveChat: (requestId: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.post(`/api/v1/teacher-chats/${requestId}/archive`)
    ),

  unarchiveChat: (requestId: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.post(`/api/v1/teacher-chats/${requestId}/unarchive`)
    ),
}
